import { ref, watch, type Ref } from "vue";
import { useRoute } from "vue-router";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import {
  ListRegularDeliveryQuery,
  ListRegularDeliveryQueryVariables,
  ListRegularDeliveryDocument,
  RegularDeliveryListInput,
  GetRegularDeliveryCountQuery,
  GetRegularDeliveryCountQueryVariables,
  GetRegularDeliveryCountDocument,
  RegularDeliveryCountInput,
  GetShopQuery,
  GetShopDocument,
  OperationInput,
  ShopListInput,
  DeliveryTimeListInput,
  DeliverableAreaDeliveryIdInput,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  ListDeliverableAreaDeliveryIdQuery,
  ListDeliverableAreaDeliveryIdDocument,
} from "@/generated/graphql";
import type { ListData, ListOptions, AreaListItem } from "../types";

interface UseRegularDeliveryListQueryParams {
  searchConditionMemberId: Ref<string | undefined | null>;
  searchConditionName: Ref<string | undefined | null>;
  searchConditionAreaCode: Ref<string | undefined | null>;
  listOptions: Ref<ListOptions>;
  listLoading: Ref<boolean>;
  listDataArray: Ref<ListData[]>;
  listDataTotalCount: Ref<number>;
  selectListCheckbox: Ref<Array<Record<string, unknown>>>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  areaListItems: Ref<AreaListItem[] | null | undefined>;
  chainSelectValues: Ref<{ chain_code: string; name: string } | null | undefined>;
  shopSelectFromValues: Ref<{ shop_code: string; name: string }>;
  syncSearchAreaCode: () => void;
  updateDisplayPagingRange: () => void;
}

/**
 * 一覧取得クエリの実行と結果管理を提供する composable。
 *
 * @returns 一覧クエリの実行結果と再取得関数
 */
export const useRegularDeliveryListQuery = ({
  searchConditionMemberId,
  searchConditionName,
  searchConditionAreaCode,
  listOptions,
  listLoading,
  listDataArray,
  listDataTotalCount,
  selectListCheckbox,
  errdialog,
  errMessage,
  areaListItems,
  chainSelectValues,
  shopSelectFromValues,
  syncSearchAreaCode,
  updateDisplayPagingRange,
}: UseRegularDeliveryListQueryParams) => {
  const route = useRoute();

  /**
   * 店舗一覧 input
   */
  const shopListInput: ShopListInput = {
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  };

  /**
   * 配達時刻一覧 input
   */
  const deliveryTimeListInput: DeliveryTimeListInput = {
    area_code: [],
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    shop_code: "",
    limit: 10000,
    offset: 0,
  };

  /**
   * 配達可能なエリア・便 input
   */
  const deliverableAreaDeliveryIdInput: DeliverableAreaDeliveryIdInput = {
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    shop_code: "",
    delivered_zip_code: "",
  };

  //  配達依頼作成 logInput
  const operationInput: OperationInput = {
    area_code: "10",
    detail: "操作詳細",
    display_category: "画面名",
    operation: "操作",
    user_id: localStorageGetUserId(),
  };

  // 店舗情報
  const shopData = ref<
    | {
        __typename?: "ShopResponse";
        phone?: string | null | undefined;
        name: string;
        name_kana: string;
        address1: string;
        address2: string;
        address3?: string | null | undefined;
        holiday_fri: number;
        holiday_mon: number;
        holiday_sat: number;
        holiday_sun: number;
        holiday_thu: number;
        holiday_tue: number;
        holiday_wed: number;
        lot_code: string;
        chain_code: string;
        created_date: string;
        created_user_id: string;
        customer_code: string;
        dss_connection_id?: number | null | undefined;
        dss_shop_code?: string | null | undefined;
        fax?: string | null | undefined;
        delivery_request_notification_emails?: string | null | undefined;
        shop_code: string;
        updated_date?: string | null | undefined;
        updated_user_id?: string | null | undefined;
        zip_code: string;
        alert_lower_threshold?: number | null | undefined;
        alert_upper_threshold?: number | null | undefined;
        shipping_label_print_flg: number;
        delivery_order_print_flg: number;
        double_deli_oricon_count?: number | null | undefined;
        charter_deli_oricon_count?: number | null | undefined;
      }
    | null
    | undefined
  >(null);
  const shopData_from_name = ref<string>("");
  const shopData_from_address = ref<string>("");
  const shopData_from_zip_code = ref<string>("");
  const shopData_to_name = ref<string>("");
  const shopData_to_address = ref<string>("");

  // 検索ボタンからの呼び出し時にwatchをスキップするフラグ
  const isSearchButtonClick = ref(false);

  /**
   * 店舗情報取得
   */
  const getShopData = async (shop_code: string) => {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      const shop_data: ApolloQueryResult<GetShopQuery> =
        await apolloClient.query<GetShopQuery>({
          query: GetShopDocument,
          variables: {
            chain_code: localStorageGetChainCode(),
            customer_code: localStorageGetCustomerCode(),
            shop_code: shop_code,
          },
        });

      return shop_data;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return undefined;
    }
  };

  /**
   * エリア情報取得
   */
  const GetAreaData = async (shop_code: string) => {
    if (shop_code == "") {
      return;
    }

    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      // エリアデータ取得処理
      const listArea: ApolloQueryResult<ListDeliveryAreaQuery> =
        await apolloClient.query<
          ListDeliveryAreaQuery,
          ListDeliveryAreaQueryVariables
        >({
          query: ListDeliveryAreaDocument,
          variables: {
            chain_code: chainSelectValues.value!.chain_code,
            customer_code: localStorageGetCustomerCode(),
            shop_code: shop_code,
          },
          fetchPolicy: "no-cache",
        });
      areaListItems.value = listArea.data.listDeliveryArea;

      areaListItems.value?.forEach((value) => {
        if (value !== undefined && value !== null) {
          deliveryTimeListInput.area_code!.push(value.area_code);
        }
      });
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = "配達エリアが存在しません。";
      return;
    }
  };

  /**
   * 配達便存在チェック
   */
  const chkDeliveryId = async () => {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      deliverableAreaDeliveryIdInput.customer_code =
        localStorageGetCustomerCode();
      deliverableAreaDeliveryIdInput.chain_code =
        chainSelectValues.value!.chain_code;
      deliverableAreaDeliveryIdInput.shop_code =
        shopSelectFromValues.value.shop_code;

      // 配達便が存在しているかチェック
      const deliverableAreaDeliveryIdInputResult: ApolloQueryResult<ListDeliverableAreaDeliveryIdQuery> =
        await apolloClient.query<ListDeliverableAreaDeliveryIdQuery>({
          query: ListDeliverableAreaDeliveryIdDocument,
          variables: {
            input: deliverableAreaDeliveryIdInput,
          },
        });
      //存在していない場合エラー
      if (
        deliverableAreaDeliveryIdInputResult.data.listDeliverableAreaDeliveryId
          ?.length == 0
      ) {
        errdialog.value = true;
        errMessage.value =
          "選択された配達先は、出荷元の配達エリアに\r\n存在しない店舗です。";
        return false;
      } else {
        return true;
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "不明なエラーが発生しました。";
      return false;
    }
  };

  /**
   * 絞り込み検索 @Click
   */
  const onClickSearch = async (): Promise<void> => {
    if (!(await isUpdateModeBack())) {
      //  errdialog.value = true;
      //  return;
    }

    selectListCheckbox.value = [];
    syncSearchAreaCode();

    // 検索ボタンをクリックした場合、ページを1に戻す（既に1ページ目の場合はスキップ）
    if (listOptions.value.page !== 1) {
      isSearchButtonClick.value = true;
      listOptions.value.page = 1;
    }

    //検索する
    await searchQuery();
  };

  /**
   * 絞り込み検索
   */
  const searchQuery = async (): Promise<void> => {
    try {
      listLoading.value = true;
      //graphql接続
      const apolloClient = await generateApolloClient();
      // 定期配送一覧 input [limit 取得件数 offset:取得開始位置]
      const listInput: RegularDeliveryListInput = {
        chain_code: localStorageGetChainCode(),
        customer_code: localStorageGetCustomerCode(),
        shop_code: localStorageGetShopCode(),
        member_id: (() => {
          const raw = searchConditionMemberId.value?.toString().trim();
          // 数字以外が含まれる場合は検索条件から除外する
          return raw && /^\d+$/.test(raw) ? Number(raw) : undefined;
        })(),
        member_name:
          searchConditionName.value !== undefined &&
          searchConditionName.value !== null &&
          searchConditionName.value !== ""
            ? searchConditionName.value
            : undefined,
        area_code:
          searchConditionAreaCode.value !== undefined &&
          searchConditionAreaCode.value !== null &&
          searchConditionAreaCode.value !== ""
            ? searchConditionAreaCode.value
            : null,
        limit: listOptions.value.itemsPerPage,
        offset: (listOptions.value.page - 1) * listOptions.value.itemsPerPage,
      };
      // 定期配送一覧作成 logInput
      operationInput.detail = "定期配送一覧";
      operationInput.display_category = "一覧表示";
      // 定期配送一覧取得
      let result: ApolloQueryResult<ListRegularDeliveryQuery>;
      try {
        result = await apolloClient.query<
          ListRegularDeliveryQuery,
          ListRegularDeliveryQueryVariables
        >({
          query: ListRegularDeliveryDocument,
          variables: {
            input: listInput,
            logInput: operationInput,
          },
          fetchPolicy: "no-cache",
        });
      } catch (err: unknown) {
        console.error(err);
        errdialog.value = true;
        errMessage.value = "定期配送一覧データの取得に失敗しました。";
        return;
      }
      if (
        result.data.listRegularDelivery === undefined ||
        result.data.listRegularDelivery === null
      ) {
        // 定期配送一覧取得 エラー
        errdialog.value = true;
        errMessage.value = "定期配送一覧データの取得に失敗しました。";
        return;
      }
      // 定期配送一覧のデータを作成
      const _listDataArray: ListData[] = [];
      const list = result.data.listRegularDelivery;
      if (list !== undefined && list !== null && list?.length > 0) {
        // 出荷元店舗、配達先店舗取得
        const shop_code_from_ary: string[] = [];
        const shop_code_from_date: { [key: string]: { shop_name: string } } = {};
        for (const value of list) {
          if (
            value !== null &&
            value !== undefined &&
            (shop_code_from_ary.length == 0 ||
              (shop_code_from_ary.length > 0 &&
                shop_code_from_ary.indexOf(value.shop_code) == -1))
          ) {
            try {
              // 出荷元店舗情報取得
              const shop_data_from = await getShopData(value.shop_code);

              shopData.value = undefined;
              shopData.value = shop_data_from!.data!.getShop!;
              shop_code_from_date[value.shop_code] = {
                shop_name: shopData.value!.name,
              };
            } catch (err: unknown) {
              console.error(err);
              errdialog.value = true;
              errMessage.value =
                err instanceof Error ? err.message : "不明なエラーが発生しました。";
              return;
            }
            shop_code_from_ary.push(value.shop_code);
          }
        }

        for (const value of list) {
          if (value !== undefined && value !== null) {
            _listDataArray.push({
              regular_delivery_no: value.regular_delivery_no,
              shopCodeFrom: value.shop_code,
              shopNameFrom: shop_code_from_date[value.shop_code].shop_name,
              memberId: value.member_id,
              memberName: value.delivery_name1 + " " + value.delivery_name2,
              deliveryAddress:
                "〒 " +
                value.delivery_zip_code +
                " " +
                value.delivery_address1 +
                value.delivery_address2 +
                value.delivery_address3,
              deliveryPhone: value.delivery_phone,
              week: "",
              mon_to_deliver: value.mon_to_deliver!,
              tue_to_deliver: value.tue_to_deliver!,
              wed_to_deliver: value.wed_to_deliver!,
              thu_to_deliver: value.thu_to_deliver!,
              fri_to_deliver: value.fri_to_deliver!,
              sat_to_deliver: value.sat_to_deliver!,
              sun_to_deliver: value.sun_to_deliver!,
              blackoutDateSetting: "",
              deliveryIdName:
                value.delivery_id === 99
                  ? value.area_name + "　定期便"
                  : value.area_name + "　" + String(value.delivery_id) + "便",
              deliveryTime:
                value.irregular_delivery_start_time.substr(0, 5) +
                "～" +
                value.irregular_delivery_end_time.substr(0, 5),
            });
          }
        }
      }

      listDataArray.value = _listDataArray;

      // 定期配送一覧件数取得
      const ListCntInput: RegularDeliveryCountInput = {
        chain_code: localStorageGetChainCode(),
        customer_code: localStorageGetCustomerCode(),
        shop_code: localStorageGetShopCode(),
        member_id: (() => {
          const raw = searchConditionMemberId.value?.toString().trim();
          // 数字以外が含まれる場合は検索条件から除外する
          return raw && /^\d+$/.test(raw) ? Number(raw) : null;
        })(),
        member_name:
          searchConditionName.value !== undefined &&
          searchConditionName.value !== null &&
          searchConditionName.value !== ""
            ? searchConditionName.value
            : undefined,
        area_code:
          searchConditionAreaCode.value !== undefined &&
          searchConditionAreaCode.value !== null &&
          searchConditionAreaCode.value !== ""
            ? searchConditionAreaCode.value
            : null,
      };

      let result_cnt: ApolloQueryResult<GetRegularDeliveryCountQuery>;
      try {
        result_cnt = await apolloClient.query<
          GetRegularDeliveryCountQuery,
          GetRegularDeliveryCountQueryVariables
        >({
          query: GetRegularDeliveryCountDocument,
          variables: {
            input: ListCntInput,
          },
          fetchPolicy: "no-cache",
        });
      } catch (err: unknown) {
        console.error(err);
        errdialog.value = true;
        errMessage.value =
          err instanceof Error
            ? err.message
            : "定期配送一覧件数データの取得に失敗しました。";
        return;
      }
      // 絞込み後の全データ数 更新
      listDataTotalCount.value =
        result_cnt!.data.getRegularDeliveryCount?.count !== undefined &&
        result_cnt!.data.getRegularDeliveryCount?.count !== null
          ? result_cnt!.data.getRegularDeliveryCount?.count
          : 0;
      // ページングの表示
      updateDisplayPagingRange();
    } catch (err: unknown) {
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return;
    } finally {
      listLoading.value = false;
    }
  };

  /**
   * 編集画面からの戻りか判断する
   * @return true:編集、false:編集以外
   */
  const isUpdateModeBack = async (): Promise<boolean> => {
    if (
      (route.params !== null &&
        route.params !== undefined &&
        route.params.searchMode) ||
      (route.query !== null &&
        route.query !== undefined &&
        route.query.searchMode)
    ) {
      return true;
    } else {
      return false;
    }
  };

  // listOptions変化時の検索
  watch(
    () => listOptions.value,
    async () => {
      // 検索ボタンからの呼び出し時はスキップ（onClickSearchで直接処理）
      if (isSearchButtonClick.value) {
        isSearchButtonClick.value = false;
        return;
      }
      listLoading.value = true;
      await searchQuery();
      listLoading.value = false;
    },
    { deep: true }
  );

  return {
    shopListInput,
    deliveryTimeListInput,
    deliverableAreaDeliveryIdInput,
    operationInput,
    shopData,
    shopData_from_name,
    shopData_from_address,
    shopData_from_zip_code,
    shopData_to_name,
    shopData_to_address,
    getShopData,
    GetAreaData,
    chkDeliveryId,
    onClickSearch,
    searchQuery,
    isUpdateModeBack,
  };
};
